import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import store from './app/store';
import { useAppSelector } from './app/hooks';
import MainLayout from './components/layout/MainLayout';
import { PageLoader } from './components/ui/Loader';

// Lazy loaded pages
const LoginPage = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Categories = lazy(() => import('./pages/Categories'));
const Stories = lazy(() => import('./pages/Stories'));
const Tags = lazy(() => import('./pages/Tags'));
const NovelCategories = lazy(() => import('./pages/NovelCategories'));
const Novels = lazy(() => import('./pages/Novels'));

// Private route component
function PrivateRoute({ children }) {
    const { isAuthenticated } = useAppSelector((state) => state.auth);
    return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Router setup
const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route
                path="/login"
                element={
                    <Suspense fallback={<PageLoader />}>
                        <LoginPage />
                    </Suspense>
                }
            />
            <Route
                path="/"
                element={
                    <PrivateRoute>
                        <MainLayout />
                    </PrivateRoute>
                }
            >
                <Route
                    index
                    element={
                        <Suspense fallback={<PageLoader />}>
                            <Dashboard />
                        </Suspense>
                    }
                />
                <Route
                    path="categories"
                    element={
                        <Suspense fallback={<PageLoader />}>
                            <Categories />
                        </Suspense>
                    }
                />
                <Route
                    path="stories"
                    element={
                        <Suspense fallback={<PageLoader />}>
                            <Stories />
                        </Suspense>
                    }
                />
                <Route
                    path="tags"
                    element={
                        <Suspense fallback={<PageLoader />}>
                            <Tags />
                        </Suspense>
                    }
                />
                <Route
                    path="novel-categories"
                    element={
                        <Suspense fallback={<PageLoader />}>
                            <NovelCategories />
                        </Suspense>
                    }
                />
                <Route
                    path="novels"
                    element={
                        <Suspense fallback={<PageLoader />}>
                            <Novels />
                        </Suspense>
                    }
                />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </>
    )
);

function AppContent() {
    return (
        <>
            <RouterProvider router={router} />
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
        </>
    );
}

function App() {
    return (
        <Provider store={store}>
            <AppContent />
        </Provider>
    );
}

export default App;
